import type { ProficiencyBandFill } from "@memorang/helpers/language-learning";
import type { IconType, LevelIconName } from "../../icon";

export type ConnectedLevel = {
	label?: string;
	icon: LevelIconName;
	progress: number;
};

export type ConnectedLevelSizing = {
	ringRadius: number;
	ringThickness: number;
	iconSize: number;
	connectorWidth: number;
	connectorLength: number;
	connectorDashLength: number;
	connectorDashGap: number;
	labelGap: number;
	labelWidth: number;
};

const FALLBACK_LEVEL_ICON: IconType = "trophy-outline";

export const getConnectedLevelSizing = (size: number): ConnectedLevelSizing => {
	const isCompact = size <= 40;

	return {
		ringRadius: size / 2,
		ringThickness: Math.max(2, Math.round(size / 12)),
		iconSize: Math.max(12, Math.round(size / 2)),
		connectorWidth: isCompact ? 4 : 6,
		connectorLength: Math.max(4, Math.round(size / 4)),
		connectorDashLength: isCompact ? 2 : 4,
		connectorDashGap: isCompact ? 2 : 4,
		labelGap: Math.max(4, Math.round(size / 10)),
		labelWidth: Math.max(64, Math.round(size * 3)),
	};
};

export const toConnectedLevels = (
	fills: ProficiencyBandFill[],
	levelIcons: LevelIconName[],
): ConnectedLevel[] =>
	fills.map((fill, index) => ({
		label: fill.label,
		icon:
			levelIcons[Math.min(index, levelIcons.length - 1)] ?? FALLBACK_LEVEL_ICON,
		progress: fill.percent,
	}));

export const withFilledPreviousLevels = (
	levels: ConnectedLevel[],
): ConnectedLevel[] => {
	if (levels.length === 0) {
		return levels;
	}

	let maxFromRight = 0;
	const alignedProgress = new Array<number>(levels.length);
	for (let i = levels.length - 1; i >= 0; i--) {
		maxFromRight = Math.max(maxFromRight, levels[i]?.progress ?? 0);
		alignedProgress[i] = maxFromRight;
	}

	let changed = false;
	const result = levels.map((level, index) => {
		const progress = alignedProgress[index] ?? 0;
		if ((level?.progress ?? 0) >= progress) {
			return level;
		}
		changed = true;
		return { ...level, progress };
	});

	return changed ? result : levels;
};
